import { noop } from 'lodash'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  Box,
  IllustrationError,
  Heading,
  Text,
  Stack,
  Inline,
  Button
} from '@machobear/design-system'
import { snakeToSentence } from '../helpers/utils'
import { ModalListingsProps } from '../helpers/types'

type Props = Pick<
  ModalListingsProps,
  'visible' | 'listingsIds' | 'page' | 'closeModal' | 'handleClose' | 'trigger'
> & {
  onDetachListings: (selectedListingIds: string[]) => void
}

const RemoveListingsModal = ({
  listingsIds,
  visible,
  trigger,
  closeModal,
  handleClose = noop,
  onDetachListings
}: Props) => {
  const { t } = useTranslation()

  const handleOnCloseRemoveListings = () => {
    handleClose()
    closeModal()
  }

  const handleSubmitRemoveListings = (selectedListingIds: string[]) => {
    onDetachListings(selectedListingIds)
    handleClose()
    closeModal()
  }

  return (
    <Dialog
      open={visible}
      onClose={handleOnCloseRemoveListings}
      title=""
      id="remove-listings"
      width="xsmall"
      illustration={<IllustrationError />}
      description={<RemoveListingsDescription listingsIds={listingsIds} trigger={trigger} />}
    >
      <Inline space="gutter" align="center">
        <Button variant="transparent" onClick={handleOnCloseRemoveListings}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button onClick={() => handleSubmitRemoveListings(listingsIds)}>
          {t('common.remove', 'Remove')}
        </Button>
      </Inline>
    </Dialog>
  )
}

type DescriptionProps = {
  listingsIds: string[]
  trigger: string
}

const RemoveListingsDescription = ({ listingsIds, trigger }: DescriptionProps) => {
  const { t } = useTranslation()

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="spaceBetween"
      gap="large"
    >
      <Stack space="small" align="center">
        <Heading level="6" align="center">
          Remove {listingsIds.length} listing{listingsIds.length > 1 ? 's' : ''} from{' '}
          {snakeToSentence(trigger)}?
        </Heading>
        <Text align="center">
          {t(
            'l.settings.view.auto_reply.not_send_selected_listings',
            'This message will not be sent for the selected listings.'
          )}
        </Text>
      </Stack>
    </Box>
  )
}

export default RemoveListingsModal
